import React__default from "react";
import { FloatingList } from "@floating-ui/react";
import clsx from "clsx";
import debounce from "debounce";
import "tabbable";
import { ScrollFade } from "../scroll-fade/scroll-fade.mjs";
import { useHorizontalScroll } from "../horizontal-scroll/use-horizontal-scroll.mjs";
import { TabsNavigationContext } from "./tabs-navigation-context.mjs";
const TabsNavigation = React__default.forwardRef(function TabsNavigation2(props, ref) {
  var _a, _b, _c;
  const {
    children,
    className,
    "aria-labelledby": labelledBy,
    size = "medium",
    align = "left",
    iconPosition,
    background,
    behaviour = "list",
    bordered = true,
    fullWidth,
    ...rest
  } = props;
  const scrollContainerRef = React__default.useRef(null);
  const elementsRef = React__default.useRef([]);
  const underlineStyles = React__default.useRef({});
  const [animatingUnderline, setAnimatingUnderline] = React__default.useState(false);
  const timeout = React__default.useRef(0);
  const currentItemRef = React__default.useRef(null);
  const updateUnderline = React__default.useCallback(
    (isResizing) => {
      var _a2;
      const activeElement = (_a2 = currentItemRef.current) == null ? void 0 : _a2.element;
      if (activeElement) {
        const style = getComputedStyle(activeElement);
        const newUnderlineProps = {
          maxWidth: size === "huge" ? parseFloat(style.width) : parseFloat(style.width) - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
          left: activeElement.offsetLeft
        };
        underlineStyles.current = newUnderlineProps;
        if (!isResizing) {
          setAnimatingUnderline(true);
          window.clearTimeout(timeout.current);
          timeout.current = window.setTimeout(
            /* istanbul ignore next */
            () => {
              setAnimatingUnderline(false);
            },
            300
          );
        }
      }
    },
    [size]
  );
  React__default.useEffect(() => {
    const resizeHandler = () => updateUnderline(true);
    const debouncedResizeHandler = debounce(resizeHandler, 100);
    window.addEventListener("resize", debouncedResizeHandler);
    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      debouncedResizeHandler.clear();
    };
  }, [updateUnderline]);
  const { scrollTo } = useHorizontalScroll({
    containerRef: scrollContainerRef,
    elementsRef
  });
  const setCurrentItem = React__default.useCallback(
    (item) => {
      var _a2;
      const prevElement = (_a2 = currentItemRef.current) == null ? void 0 : _a2.element;
      if (item.element && item.element !== prevElement) {
        const skipAnimation = !prevElement;
        currentItemRef.current = item;
        updateUnderline(skipAnimation);
      }
    },
    [updateUnderline]
  );
  const handleArrowClick = (_event, direction) => scrollTo(direction);
  const currentItemPuropse = (_a = currentItemRef.current) == null ? void 0 : _a.purpose;
  return /* @__PURE__ */ React__default.createElement(TabsNavigationContext.Provider, { value: React__default.useMemo(() => ({ setCurrentItem, behaviour }), [setCurrentItem, behaviour]) }, /* @__PURE__ */ React__default.createElement(
    "div",
    {
      className: clsx(
        "tabs-navigation",
        "tabs-navigation--".concat(size),
        "tabs-navigation--".concat(align),
        {
          "tabs-navigation--bordered": bordered,
          "tabs-navigation--full-width": fullWidth,
          ["tabs-navigation--background-".concat(background)]: background && size === "huge",
          ["tabs-navigation--icons-".concat(iconPosition)]: iconPosition
        },
        className
      ),
      ...rest
    },
    /* @__PURE__ */ React__default.createElement(
      ScrollFade,
      {
        className: "tabs-navigation__scroll",
        hasArrows: { enabled: true, focusable: false },
        elementRef: (el) => scrollContainerRef.current = el,
        onArrowClick: handleArrowClick
      },
      /* @__PURE__ */ React__default.createElement("div", { className: "tabs-navigation__scroll-inner" }, !!(animatingUnderline || underlineStyles.current.maxWidth) && /* @__PURE__ */ React__default.createElement(
        "span",
        {
          className: clsx("tabs-navigation__underline", {
            ["tabs-navigation__underline--type-".concat(currentItemPuropse)]: !!currentItemPuropse,
            "is-animating": animatingUnderline
          }),
          style: {
            maxWidth: (_b = underlineStyles.current.maxWidth) != null ? _b : 0,
            left: (_c = underlineStyles.current.left) != null ? _c : 0
          }
        }
      ), /* @__PURE__ */ React__default.createElement(FloatingList, { elementsRef }, /* @__PURE__ */ React__default.createElement("ul", { className: "tabs-navigation__inner", ref, role: behaviour === "tabs" ? "tablist" : "list", "aria-labelledby": labelledBy }, children)))
    )
  ));
});
export {
  TabsNavigation
};
